import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { USD_DECIMALS } from "config/factors";
import { formatAmountHuman } from "lib/numbers";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";

import { getMarketPoolName } from "../../../../domain/synthetics/markets/utils";
import { CompositionItem, CompositionType, getCompositionPercentage } from "../hooks/useCompositionData";

interface Props<T extends CompositionType> {
  composition: CompositionItem[];
  compositionType: T;
}

const CLOSED_COUNT = 5;

export function CompositionTable<T extends CompositionType>({ composition, compositionType }: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);

  // const columns = useMemo(() => {
  //   if (compositionType === "market") {
  //     return [t`MARKET`, t`TVL/CAP`, t`COMP.`];
  //   }

  //   return [t`COLLATERAL`, t`COMP.`];
  // }, [compositionType]);

  const sum = useMemo(() => {
    return composition.reduce(
      (acc, item) => (item.type === "market" ? acc + item.gmBalanceUsd : acc + item.amount),
      0n
    );
  }, [composition]);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const isMobile = usePoolsIsMobilePage();

  const filteredComposition = useMemo(() => {
    if (isMobile && !isOpen) {
      return composition.slice(0, CLOSED_COUNT);
    }

    return composition;
  }, [composition, isMobile, isOpen]);

  return (
    <div
      className={cx("flex w-full flex-col", {
        "gap-24": compositionType === "backing",
        "gap-14": compositionType === "market",
      })}
    >
      {/* <table className="w-full"> */}
      {/* <thead>
          <TableTheadTr bordered>
            {columns.map((column) => (
              <TableTh key={column} className="sticky top-0 bg-slate-800">
                <Trans>{column}</Trans>
              </TableTh>
            ))}
          </TableTheadTr>
        </thead> */}
      {/* <tbody> */}
      {filteredComposition.map((item) => (
        <CompositionTableRow
          key={item.type === "market" ? item.market.marketTokenAddress : `${item.token.address}-${item.side}`}
          item={item}
          sum={sum}
        />
      ))}
      {/* </tbody> */}
      {/* </table> */}
      {isMobile && composition.length > CLOSED_COUNT ? (
        <div className="flex flex-row items-center justify-between px-16 pb-20" onClick={toggleOpen}>
          <span className="text-gray-lighter">{isOpen ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}</span>
          {isOpen ? <FaChevronUp size={8} /> : <FaChevronDown size={8} />}
        </div>
      ) : null}
    </div>
  );
}

const CompositionTableRow = ({ item, sum }: { item: CompositionItem; sum: bigint }) => {
  // const tokenColor =
  //   TOKEN_COLOR_MAP[item.type === "market" ? item.market.indexToken.symbol : item.token.symbol] ??
  //   TOKEN_COLOR_MAP.default;

  const tokenColor = item.type === "market" ? "#00BD79" : item.side === "long" ? "#00BD79" : "#EF476F";

  const tokenCircleStyles = useMemo(() => {
    return {
      backgroundColor: tokenColor,
    };
  }, [tokenColor]);

  const BackingData = () => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center text-[13px] font-medium">
          <span className="mr-8 inline-block h-14 w-14 shrink-0 rounded-10" style={tokenCircleStyles} />
          {item.type === "backing" && (
            <span className="capitalize">
              {item.side}: <span className="overflow-hidden text-ellipsis whitespace-nowrap">{item.token.symbol}</span>
            </span>
          )}
        </div>
        {item.type === "backing" && (
          <div className="pl-22">
            <span className="text-[13px] font-medium text-gray-lighter">
              {getCompositionPercentage(item.amount, sum)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const MarketData = () => {
    return (
      <div
        className={cx("flex flex-col", {
          "gap-24": item.type === "backing",
          "gap-14": item.type === "market",
        })}
      >
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-medium text-white">
            <Trans>Market</Trans>
          </span>
          <div className="flex flex-row items-center">
            <span className="mr-8 inline-block h-14 w-14 shrink-0 rounded-10" style={tokenCircleStyles} />
            {/* <TokenIcon
            symbol={item.type === "market" ? item.market.indexToken.symbol : item.token.symbol}
            displaySize={24}
          /> */}
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium text-gray-lighter">
              {item.type === "market" && <>{getMarketPoolName(item.market).replace(/\s*\/\s*/g, "/")}</>}
            </span>
          </div>
        </div>
        {item.type === "market" && (
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-white">
              <Trans>TVL/CAP</Trans>
            </span>
            <span className="text-[13px] font-medium text-gray-lighter">
              {formatAmountHuman(item.tvl[0], USD_DECIMALS, true, 1)}/
              {formatAmountHuman(item.tvl[1], USD_DECIMALS, true, 1)}
            </span>
          </div>
        )}
        {item.type === "market" && (
          <div className="flex flex-col">
            <span className="text-[13px] font-medium text-white">
              <Trans>Composition</Trans>
            </span>
            <span className="text-[13px] font-medium text-gray-lighter">
              {getCompositionPercentage(item.gmBalanceUsd, sum)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  return item.type === "backing" ? <BackingData /> : <MarketData />;
};
